// 담당: 현정
// 데이터 상태 6종 — 판정(순수 함수) + 캡션 렌더. SNS 데이터 유효성 v1 §5.0.
//
// 이 파일이 상태 어휘의 단일 원천이다. 화면 컴포넌트는 문구를 직접 쓰지 않고 여기서 받는다.
//   ① OK · ② 저신뢰 · ②-0 집계 콘텐츠 없음 · ③ 미수집 · ④ 미제공 · ⑤ 확정 대기 · ⑥ 최초 수집 전
// 어휘(사실)는 BE `reason` 코드가 원천이고, 임계(표본 5건) 적용만 FE가 한다.
//
// 판정 우선순위: ④ 미제공 > ③ 미수집 > ⑥ 최초 수집 전 > ⑤ 확정 대기 > ②-0 > ② 저신뢰 > ① OK
//   단 **값이 유한하면 값을 숨기지 않는다**(GR3 — 정직화가 기능 후퇴가 되면 안 됨).
//   결측 사유 4종(③④⑤⑥)은 값이 없을 때만 적용되고, 값이 있으면 표본 기반 ①/②로 간다.
//   BE 필드 미도달(sampleSize·reason 모두 없음) → 'unknown' = 캡션 없음 = 현행 동작(R9 폴백).
import type { ConfirmDays, MetricReason } from '../creatorsApi'

/** 표본 위생선 — n≥5는 값만, 1≤n<5는 값+저신뢰 캡션. 신뢰구간 보증이 아니라 "숫자로 부를 최소선". */
export const LOW_SAMPLE_MIN = 5

export type MetricState =
  | 'ok' // ①
  | 'low_sample' // ②
  | 'no_content' // ②-0
  | 'not_collected' // ③
  | 'platform_unsupported' // ④
  | 'pending_confirmation' // ⑤
  | 'never_collected' // ⑥
  | 'unknown' // 판정 입력 미도달 — 캡션 없이 현행 표기 유지

export interface MetricInput {
  /** 지표 값. null/비유한값이면 결측으로 본다. */
  value?: number | null
  /** 모수. BE 미도달이면 undefined(≠ 0). */
  sampleSize?: number | null
  /** BE 상태 코드. */
  reason?: MetricReason | null
}

export interface MetricStatus {
  state: MetricState
  /** 값을 그대로 렌더할지(false면 '—'). */
  hasValue: boolean
  /** 표시 문구. null이면 캡션 슬롯 비움. */
  caption: string | null
  /** 'state' = 판단에 직접 쓰는 사유(fg-2) · 'meta' = 정상 모수 참고(fg-3) */
  tone: 'state' | 'meta'
  /** 경고 도트 — 저신뢰(②)에만. 나머지 결측은 값 자리의 '—'가 이미 신호다. */
  dot: boolean
}

/** 확정일 표기 — "15" / "5~15". 소스 없으면 null(하드코딩 금지 → 문구에서 통째로 빠진다). */
export function confirmDaysText(confirmDays?: ConfirmDays | null): string | null {
  if (!confirmDays) return null
  const { min, max } = confirmDays
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null
  return min === max ? `${min}` : `${min}~${max}`
}

/** 결측(값 자리가 '—')인 상태인지. 블록 각주 집약 판단에 쓴다. */
export function isMissing(state: MetricState): boolean {
  return (
    state === 'no_content' ||
    state === 'not_collected' ||
    state === 'platform_unsupported' ||
    state === 'pending_confirmation' ||
    state === 'never_collected'
  )
}

/** 상태 판정 — 순수 함수. 렌더 의존 0(검증 스크립트가 이 함수만 때린다). */
export function resolveMetric(
  input: MetricInput,
  opts?: { confirmDays?: ConfirmDays | null },
): MetricStatus {
  const { value, sampleSize, reason } = input
  const n = sampleSize == null || !Number.isFinite(sampleSize) ? null : sampleSize
  const hasValue = value != null && Number.isFinite(value)

  if (hasValue) {
    // 값이 있으면 결측 사유보다 값이 우선한다(숨기지 않는다).
    if (n == null) return status('ok', true, null, 'meta', false)
    if (n <= 0) return status('ok', true, null, 'meta', false) // 값은 있는데 n=0 → 모순, 캡션 생략
    if (n < LOW_SAMPLE_MIN) return status('low_sample', true, `표본 ${n}건`, 'state', true)
    return status('ok', true, `표본 ${n}건`, 'meta', false)
  }

  // 결측 — 우선순위대로 배타 판정.
  if (reason === 'platform_unsupported')
    return status('platform_unsupported', false, '플랫폼 미제공', 'state', false)
  if (reason === 'not_collected') return status('not_collected', false, '수집 대상 아님', 'state', false)
  if (reason === 'never_collected') return status('never_collected', false, '수집 예정', 'state', false)
  if (reason === 'pending_confirmation') {
    const days = confirmDaysText(opts?.confirmDays)
    return status(
      'pending_confirmation',
      false,
      days ? `확정 대기(발행 ${days}일)` : '확정 대기',
      'state',
      false,
    )
  }
  if (n === 0) return status('no_content', false, '집계 콘텐츠 없음', 'state', false)
  // 모순 조합 방어 — 값이 없는데 사유가 low_sample이고 모수는 1건 이상인 경우(예: 표본은 있으나
  //   조회수가 전량 NULL). BE가 이 조합을 만들지 않는다고 보장하지만, 방어선은 FE에도 둔다.
  //   여기서 막지 않으면 'unknown'으로 떨어져 **이유 없는 결측**이 화면에 남는다(A4 위반).
  //   n=0은 위 ②-0에서 이미 잡혔으므로 여기 도달하는 건 n ≥ 1이다.
  if (reason === 'low_sample' && n != null) return status('low_sample', false, `표본 ${n}건`, 'state', true)
  return status('unknown', false, null, 'meta', false)
}

function status(
  state: MetricState,
  hasValue: boolean,
  caption: string | null,
  tone: 'state' | 'meta',
  dot: boolean,
): MetricStatus {
  return { state, hasValue, caption, tone, dot }
}

// 결측 사유를 셀 1줄로 합치는 일은 ./platformFormat 의 missingCaption 이 맡는다.
// 여기 두면 "롱/숏"이 고정돼 TikTok·Instagram 이 없는 포맷의 사유를 말하게 된다.
// 이 모듈은 상태 **판정**만 하고, 슬롯 어휘는 모른다(platformFormat 이 이 모듈을 import 한다).

/** 상태 캡션 1줄 — 도트(저신뢰)는 경고색, 텍스트는 항상 중립(11px 경고색은 AA 미달). */
export function MetricCaption({
  status: st,
  className,
}: {
  status: MetricStatus
  className?: string
}) {
  if (!st.caption) return null
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 font-text text-[11px] leading-[1.45]',
        st.tone === 'state' ? 'font-medium text-fg-2' : 'text-fg-3',
        className ?? '',
      ]
        .join(' ')
        .trim()}
    >
      {st.dot && (
        <span
          className="h-1.5 w-1.5 flex-none rounded-r-pill bg-status-warning"
          role="img"
          aria-label="저신뢰"
        />
      )}
      {st.caption}
    </span>
  )
}

/** 텍스트 전용 캡션(각주·title 등 문자열이 필요한 자리). */
export function MetricNote({ text, className }: { text: string; className?: string }) {
  return (
    <span
      className={['font-text text-[11px] font-medium leading-[1.45] text-fg-2', className ?? '']
        .join(' ')
        .trim()}
    >
      {text}
    </span>
  )
}
